import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { TableWrapper, StyledTable, ErrorMessage, ResizeHandle, HeaderCell } from './tableStyles';
import { MemoizedTableCell } from './TableCell';
import { processTableData, getColumnLabel, validateTableData } from './tableUtils';
import { TableToolbar } from './TableToolbar';
import { RowColumnMenu } from './RowColumnMenu';

export const Table = ({
  data,
  showHeaders = true,
  showRowNumbers = true,
  editable = true,
  onCellChange,
  onCellClick,
  maxHeight,
  emptyCellValue = '-',
  className,
  showValidationErrors = true,
  onColumnWidthsCalculated,
  storedColumnWidths = {},
  onColumnResize,
  onInsertColumn,
  onDeleteRow,
  onDeleteColumn,
  onInsertRow,
  onInsertColumnAt,
}) => {
  const tableRef = useRef(null);
  const [resizing, setResizing] = useState(null); // { columnIndex, startX, startWidth }
  const [activeCell, setActiveCell] = useState(null); // { rowIndex, columnIndex }
  const [menu, setMenu] = useState(null); // { type: 'row' | 'column', index: number, position: { top, left } }
  const prevMaxColRef = useRef(0); // Track previous column count
  const validationErrors = useMemo(() => validateTableData(data), [data]);

  // Process data into 2D grid
  const { rows, maxRow, maxCol, cellMetadata } = useMemo(() => processTableData(data), [data]);

  // Measure and store column widths after render
  useEffect(() => {
    if (!tableRef.current || !onColumnWidthsCalculated || rows.length === 0) {
      return;
    }

    // Small delay to ensure rendering is complete
    const timeoutId = setTimeout(() => {
      const table = tableRef.current;
      if (!table) return;

      // Get all header cells or first row cells to measure column widths
      const headerRow = table.querySelector('thead tr');
      const firstBodyRow = table.querySelector('tbody tr');
      const cellsToMeasure = headerRow?.children || firstBodyRow?.children;

      if (!cellsToMeasure) return;

      const measuredWidths = {};
      let hasUnmeasuredColumns = false;

      // Start from 0 or 1 depending on whether row numbers are shown
      const startIndex = showRowNumbers ? 1 : 0;

      for (let i = startIndex; i < cellsToMeasure.length; i++) {
        const columnIndex = i - startIndex;
        const cell = cellsToMeasure[i];

        // Only measure if not already stored
        if (storedColumnWidths[columnIndex] === undefined) {
          const width = Math.max(60, cell.offsetWidth); // Minimum width of 60px
          measuredWidths[columnIndex] = width;
          hasUnmeasuredColumns = true;
        }
      }

      // Only call callback if we measured new columns
      if (hasUnmeasuredColumns) {
        onColumnWidthsCalculated(measuredWidths);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [rows, maxCol, onColumnWidthsCalculated, storedColumnWidths, showRowNumbers]);

  // Scroll new column into view when column is added
  useEffect(() => {
    // Check if a column was added (maxCol increased)
    if (maxCol > prevMaxColRef.current && prevMaxColRef.current > 0 && maxCol - prevMaxColRef.current === 1) {
      const newColumnIndex = maxCol - 1;

      // Wait for rendering to complete
      setTimeout(() => {
        if (!tableRef.current) return;

        const headerRow = tableRef.current.querySelector('thead tr');
        if (!headerRow) return;

        // Account for row number column if present
        const cellIndex = showRowNumbers ? newColumnIndex + 1 : newColumnIndex;
        const newColumnHeader = headerRow.children[cellIndex];
        if (newColumnHeader) {
          newColumnHeader.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }, 150); // Delay to ensure DOM is updated
    }

    // Update previous column count
    prevMaxColRef.current = maxCol;
  }, [maxCol, showRowNumbers]);

  // Column resize handlers
  const handleResizeStart = useCallback(
    (columnIndex, e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = storedColumnWidths[columnIndex] || e.target.parentElement.offsetWidth;
      setResizing({ columnIndex, startX, startWidth });
    },
    [storedColumnWidths]
  );

  const handleResizeMove = useCallback(
    (e) => {
      if (!resizing) return;

      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta); // Minimum width of 60px

      if (onColumnResize) {
        onColumnResize(resizing.columnIndex, newWidth);
      }
    },
    [resizing, onColumnResize]
  );

  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);

  // Add global mouse event listeners for resize
  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Handle cell click to set active cell
  const handleCellClickInternal = useCallback(
    (rowIndex, columnIndex) => {
      setActiveCell({ rowIndex, columnIndex });
      if (onCellClick) {
        onCellClick(rowIndex, columnIndex);
      }
    },
    [onCellClick]
  );

  // Handle row number click
  const handleRowNumberClick = useCallback((rowIndex, event) => {
    event.stopPropagation();
    setMenu({
      type: 'row',
      index: rowIndex,
      position: {
        top: event.clientY,
        left: event.clientX,
      },
    });
  }, []);

  // Handle column header click
  const handleColumnHeaderClick = useCallback((columnIndex, event) => {
    event.stopPropagation();
    setMenu({
      type: 'column',
      index: columnIndex,
      position: {
        top: event.clientY,
        left: event.clientX,
      },
    });
  }, []);

  // Close menu
  const handleCloseMenu = useCallback(() => {
    setMenu(null);
  }, []);

  // Handle delete row
  const handleDeleteRow = useCallback(
    (rowIndex) => {
      if (onDeleteRow) {
        onDeleteRow(rowIndex);
      }
    },
    [onDeleteRow]
  );

  // Handle delete column
  const handleDeleteColumn = useCallback(
    (columnIndex) => {
      if (onDeleteColumn) {
        onDeleteColumn(columnIndex);
      }
    },
    [onDeleteColumn]
  );

  // Handle insert row
  const handleInsertRow = useCallback(
    (rowIndex) => {
      if (onInsertRow) {
        onInsertRow(rowIndex);
      }
    },
    [onInsertRow]
  );

  // Handle insert column
  const handleInsertColumnAt = useCallback(
    (columnIndex) => {
      if (onInsertColumnAt) {
        onInsertColumnAt(columnIndex);
      }
    },
    [onInsertColumnAt]
  );

  // Show validation errors if any
  if (showValidationErrors && validationErrors.length > 0) {
    return (
      <TableWrapper className={className}>
        <ErrorMessage>
          <strong>Table Data Validation Errors:</strong>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </ErrorMessage>
      </TableWrapper>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  // Get metadata for active cell
  const activeCellMetadata = activeCell ? cellMetadata[`${activeCell.rowIndex}-${activeCell.columnIndex}`] || {} : {};

  return (
    <TableWrapper maxHeight={maxHeight} className={className}>
      <TableToolbar activeCell={activeCell} activeCellMetadata={activeCellMetadata} data={data} />
      <StyledTable ref={tableRef}>
        <colgroup>
          {showRowNumbers && <col style={{ width: '40px' }} />}
          {Array(maxCol)
            .fill(0)
            .map((_, colIndex) => {
              const width = storedColumnWidths[colIndex];
              const style = width ? { width: `${width}px` } : {};
              return <col key={colIndex} style={style} />;
            })}
        </colgroup>
        {showHeaders && (
          <thead>
            <tr>
              {showRowNumbers && <th></th>}
              {Array(maxCol)
                .fill(0)
                .map((_, colIndex) => (
                  <HeaderCell key={colIndex} onClick={(e) => handleColumnHeaderClick(colIndex, e)}>
                    {getColumnLabel(colIndex)}
                    <ResizeHandle onMouseDown={(e) => handleResizeStart(colIndex, e)} />
                  </HeaderCell>
                ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {showRowNumbers && (
                <td className="row-number" onClick={(e) => handleRowNumberClick(rowIndex, e)}>
                  {rowIndex + 1}
                </td>
              )}
              {row.map((cellValue, colIndex) => {
                const metadataKey = `${rowIndex}-${colIndex}`;
                const metadata = cellMetadata[metadataKey] || {};
                const isActive = activeCell?.rowIndex === rowIndex && activeCell?.columnIndex === colIndex;

                return (
                  <MemoizedTableCell
                    key={`${rowIndex}-${colIndex}`}
                    value={cellValue}
                    formula={metadata.formula}
                    rowIndex={rowIndex}
                    columnIndex={colIndex}
                    editable={editable}
                    onChange={onCellChange}
                    onCellClick={handleCellClickInternal}
                    emptyCellValue={emptyCellValue}
                    type={metadata.type}
                    bold={metadata.bold}
                    italic={metadata.italic}
                    underline={metadata.underline}
                    strike={metadata.strike}
                    backgroundColor={metadata.backgroundColor}
                    textColor={metadata.textColor}
                    isActive={isActive}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </StyledTable>
      {menu && (
        <RowColumnMenu
          type={menu.type}
          index={menu.index}
          position={menu.position}
          onClose={handleCloseMenu}
          onDelete={menu.type === 'row' ? handleDeleteRow : handleDeleteColumn}
          onInsertBefore={menu.type === 'row' ? handleInsertRow : handleInsertColumnAt}
          onInsertAfter={
            menu.type === 'row' ? (index) => handleInsertRow(index + 1) : (index) => handleInsertColumnAt(index + 1)
          }
        />
      )}
    </TableWrapper>
  );
};
